import LayoutContent from "../../LayoutContent";
import Header from "../../Header/Header";
import React, { useState, useEffect, useRef } from "react";
import Breadcrumb from "../../Header/Breadcrumb";
import MtiButton from "../../Buttons/MtiButton";
import SettingsNavSubMenu from "../SettingsNavSubMenu";
import TabPanel from "../../PlateTypeEditor/TabPanel";
import { useConfirm } from "../../Hooks/WindowHooks";
import Hand2CorrectionComponent from "./Hand2Correction/Hand2CorrectionComponent";
import Hand2OffsetCalibrationComponent from "./Hand2OffsetCalibration/Hand2OffsetCalibrationComponent";
import HeaderDropDownButton from "../../Header/HeaderDropDownButton";
import IconMoreAction from "../../../Icons/TopCommandPanel/MoreAction";
import SvgExport from "../../../Icons/TopCommandPanel/Export";
import SvgImport from "../../../Icons/TopCommandPanel/Import";
import CalibrationApi from "../../../api/CalibrationApi";
import HeaderButton from "../../Header/HeaderButton";
import Refresh from "../../../Icons/Refresh";
import ModalNotificationView from "../../Common/ModalNotification";
import { ENotificationType } from "../../../Model/ENotificationType";
import { sendNotification } from "../../../redux/actions/NotificationAction";
import { useDispatch } from "react-redux";
import PsrAlignmentCalibrationComponent from "./PsrAlignmentCalibration/PsrAlignmentCalibrationComponent";
import HotelCalibrationComponent from "./HotelCalibrationRi1000/HotelCalibrationComponent";
import HotelHeightCalibrationComponent from "./HotelHeightCalibration/HotelHeightCalibrationComponent";
import { CALIBRATION_LOG_KEY } from "../../Hooks";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { ESuiteMode } from "../../../Model/ModuleState";
import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import useCalibrationLog from "../../Hooks/useCalibrationLog";
import config from "../../../config";

interface HubRefParams {
  hubConnection: HubConnection | null;
}

type StorageCalibrationType =
  | "PSR ALIGNMENT"
  | "HAND 2 OFFSET"
  | "HAND 2 CORRECTION"
  | "HOTEL"
  | "HOTEL HEIGHT";

export const calibrationTabMap = {
  psrAlignment: "PSR ALIGNMENT",
  hand2Offset: "HAND 2 OFFSET",
  hand2Correction: "HAND 2 CORRECTION",
  hotel: "HOTEL",
  hotelHeight: "HOTEL HEIGHT",
};

export const calibrationSignalRMap = {
  psrAlignment: "PsrAlignment",
  hand2Offset: "Hand2Offset",
  hand2Correction: "Hand2Correction",
  hotel: "Hotel",
  hotelHeight: "HotelHeight",
};

const storageCalibrationTabs = Object.values(calibrationTabMap).map(
  (tabname) => tabname
);

function getCachedSelectedTab(): StorageCalibrationType | null {
  const data = localStorage.getItem("TAB");
  if (data == null) return null;

  return data as StorageCalibrationType;
}

const EXPORT = "Export";
const IMPORT = "Import";
export default function SettingsStorageRi1000() {
  const dispatch = useDispatch();
  const [onApply, setOnApply] = useState({
    execute: () => {},
    disabled: true,
  });
  const [onCancel, setOnCancel] = useState({
    execute: () => {},
    disabled: true,
  });

  const [clickableItems, setClickableItems] = useState(
    new Map<string, boolean>([
      ["PSR ALIGNMENT", false],
      ["HAND 2 OFFSET", false],
      ["HAND 2 CORRECTION", false],
      ["HOTEL", false],
      ["HOTEL HEIGHT", false],
    ])
  );

  const enableTabs = () => {
    setClickableItems(
      new Map<string, boolean>([
        ["PSR ALIGNMENT", true],
        ["HAND 2 OFFSET", true],
        ["HAND 2 CORRECTION", true],
        ["HOTEL", true],
        ["HOTEL HEIGHT", true],
      ])
    );
  };

  const disableTabs = () => {
    setClickableItems(
      new Map<string, boolean>([
        ["PSR ALIGNMENT", false],
        ["HAND 2 OFFSET", false],
        ["HAND 2 CORRECTION", false],
        ["HOTEL", false],
        ["HOTEL HEIGHT", false],
      ])
    );
  };

  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.Error;
  const isAutoCalibrationRunnning = suiteMode === ESuiteMode.AutoCalibrating;

  useEffect(() => {
    if (suiteMode === ESuiteMode.AutoCalibrating) {
      disableTabs();
    } else {
      enableTabs();
    }
  }, [suiteState]);

  const [showResetWarning, setShowResetWarning] = useState(false);
  const resetAllCalibration = async () => {
    CalibrationApi.resetPsrAlignmentCalibration();
    CalibrationApi.resetHand2OffsetCalibration();
    CalibrationApi.resetHand2ThetaCorrection();
    CalibrationApi.resetHotelCalibrationResult();
    CalibrationApi.resetStorageHeightCalibration();
  };

  const [isCalibrationUpdated, setIsCalibrationUpdated] = useState(false);

  const previousCalibrationTab = getCachedSelectedTab();
  const [storageCalibrationType, setStorageCalibrationType] = useState(
    previousCalibrationTab ?? storageCalibrationTabs[0]
  );
  const [runningTabKey, setRunningTabKey] = useState<string>("");
  const [connection, setConnection] = useState<HubConnection>();
  const [_, setLogs] = useCalibrationLog();

  useEffect(() => {
    const urlConnection = config.SIGNALR_URL + "/calibration";
    const hubConnection = new HubConnectionBuilder()
      .withUrl(urlConnection)
      .build();
    hubConnection
      .start()
      .catch((err) =>
        sendNotification(
          ENotificationType.Error,
          "Calibration Messages Connection Error: " + err
        )
      );
    setConnection(hubConnection);
    return () => {
      hubConnection?.stop();
      setConnection(hubConnection);
    };
  }, []);

  useEffect(() => {
    if (connection) {
      connection.on(`CalibrationStatusUpdate[${runningTabKey}]`, (message) =>
        setLogs(message, runningTabKey)
      );
    }
  }, [connection, runningTabKey]);

  useEffect(() => {
    localStorage.setItem("TAB", storageCalibrationType);
  }, [storageCalibrationType]);

  const selectedTabIndex = storageCalibrationTabs.findIndex(
    (h) => h === storageCalibrationType
  );

  const userConfirmLeaveTab = useConfirm(
    "Changes are not saved, are you sure you want to leave Settings - Storage - X"
  );
  userConfirmLeaveTab.setOnCancel(() => {
    return;
  });

  const onClickTab = (header: string) => {
    // Reset export/import handler
    setExportHandler({
      execute: () => {},
      disabled: true,
    });

    setImportHandler({
      execute: () => {},
      disabled: true,
    });

    if (
      isCalibrationUpdated &&
      storageCalibrationType !== (header as StorageCalibrationType)
    ) {
      userConfirmLeaveTab.setOnOk(() => {
        setStorageCalibrationType(header as StorageCalibrationType);
        setIsCalibrationUpdated(false);
      });
      userConfirmLeaveTab.showConfirmationAlert();
      return;
    } else {
      setStorageCalibrationType(header as StorageCalibrationType);
    }
  };

  useEffect(() => {
    switch (storageCalibrationType) {
      case "HOTEL":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Hotel Calibration"
        );
        break;
      case "PSR ALIGNMENT":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - PSR Alignment Calibration"
        );
        break;
      case "HAND 2 OFFSET":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Hotel Offset Calibration"
        );
        break;
      case "HAND 2 CORRECTION":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Hand 2 Correction"
        );
        break;
      case "HOTEL HEIGHT":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Hotel Height Calibration"
        );
        break;
    }
  }, [storageCalibrationType]);

  const [importHandler, setImportHandler] = useState({
    execute: (fileToImport: any) => {},
    disabled: true,
  });
  const [exportHandler, setExportHandler] = useState({
    execute: () => {},
    disabled: true,
  });

  const [resetHandler, setResetHandler] = useState({
    execute: () => {},
    disabled: true,
  });

  const moreActionOptions = [
    {
      value: EXPORT,
      display: "Export",
      icon: <SvgExport />,
      disabled: exportHandler.disabled,
    },
    {
      value: IMPORT,
      display: "Import",
      icon: <SvgImport />,
      disabled: importHandler.disabled,
    },
  ];

  const importRef = useRef<any>();
  const optionClickedHandler = (name: string) => {
    switch (name) {
      case EXPORT:
        if (exportHandler?.execute != undefined) {
          exportHandler.execute();
        }
        break;
      case IMPORT:
        (importRef.current as any).click();
        break;
    }
  };

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              { title: "Storage", to: "/settings/storage", disabled: false },
            ]}
          />
          <Header.RightSide>
            <HeaderButton
              label="Reset Calibration"
              id="reset-calibration-btn"
              icon={<Refresh width={43} height={34} />}
              onClick={() => {
                setShowResetWarning(true);
              }}
              disabled={isAutoCalibrationRunnning}
            />
            {(!importHandler.disabled || !exportHandler.disabled) && (
              <HeaderDropDownButton
                id="calibration-more-actions"
                icon={<IconMoreAction />}
                label={"More Actions"}
                onChange={optionClickedHandler}
                iconDisplayValues={moreActionOptions}
              />
            )}

            <input
              ref={importRef}
              type="file"
              id="input_file_to_import"
              accept=".csv"
              onInput={(e: any) => {
                if (importHandler?.execute != undefined) {
                  importHandler.execute(e.target.files[0]);
                  (importRef.current as any).value = "";
                }
              }}
              style={{ display: "none" }}
            />

            <MtiButton
              size="large"
              variant="secondary"
              style={{
                margin: "auto 10px auto 25px",
              }}
              disabled={onCancel.disabled}
              onClick={onCancel.execute}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              disabled={onApply.disabled}
              style={{
                margin: "auto 16px auto 10px",
              }}
              onClick={() => {
                localStorage.removeItem(CALIBRATION_LOG_KEY);
                onApply.execute();
              }}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>

      <LayoutContent.SubMenu>
        <SettingsNavSubMenu />
      </LayoutContent.SubMenu>

      <LayoutContent.Content>
        <TabPanel
          id="storage_tab"
          collapsible={false}
          variant="alternate"
          headerHeight={48}
          headerItemWidth={221}
          style={{ height: "100%", marginLeft: 12 }}
          onClickTab={onClickTab}
          allowChange={false}
          defaultSelectedIndex={selectedTabIndex}
          activeTabStyle={{
            padding: "0px",
          }}
          headerStyle={{
            margin: "0px 0px",
          }}
          headersCanBeClicked={clickableItems}
        >
          {{
            "PSR ALIGNMENT": (
              <PsrAlignmentCalibrationComponent
                setOnApply={setOnApply}
                setOnCancel={setOnCancel}
                setIsCalibrationUpdated={setIsCalibrationUpdated}
                setResetHandler={setResetHandler}
                setRunningTabKey={setRunningTabKey}
              />
            ),
            "HAND 2 OFFSET": (
              <Hand2OffsetCalibrationComponent
                setOnApply={setOnApply}
                setOnCancel={setOnCancel}
                setIsCalibrationUpdated={setIsCalibrationUpdated}
                setResetHandler={setResetHandler}
                setRunningTabKey={setRunningTabKey}
              />
            ),
            "HAND 2 CORRECTION": (
              <Hand2CorrectionComponent
                setOnApply={setOnApply}
                setOnCancel={setOnCancel}
                setIsCalibrationUpdated={setIsCalibrationUpdated}
                setResetHandler={setResetHandler}
                setRunningTabKey={setRunningTabKey}
              />
            ),
            HOTEL: (
              <HotelCalibrationComponent
                setOnApply={setOnApply}
                setOnCancel={setOnCancel}
                setImportHandler={setImportHandler}
                setExportHandler={setExportHandler}
                setIsCalibrationUpdated={setIsCalibrationUpdated}
                setResetHandler={setResetHandler}
                setRunningTabKey={setRunningTabKey}
              />
            ),
            "HOTEL HEIGHT": (
              <HotelHeightCalibrationComponent
                setOnApply={setOnApply}
                setOnCancel={setOnCancel}
                setIsCalibrationUpdated={setIsCalibrationUpdated}
                setResetHandler={setResetHandler}
                setRunningTabKey={setRunningTabKey}
              />
            ),
          }}
        </TabPanel>
      </LayoutContent.Content>
      <ModalNotificationView
        headerText="Clear Calibration Result"
        bodyTitleText="Are you sure to reset calibration result?"
        bodySubTitleText="All calibration result will removed from system"
        type={ENotificationType.Warning}
        show={showResetWarning}
        onHide={() => {}}
        onClickOk={() => {
          resetAllCalibration()
            .then(() => {
              resetHandler?.execute();
            })
            .catch(() => {
              dispatch(
                sendNotification(
                  ENotificationType.Error,
                  "Resetting calibration failed"
                )
              );
            });
          setShowResetWarning(false);
        }}
        onClickCancel={() => {
          setShowResetWarning(false);
        }}
        okLabel="OK"
        cancelLabel="CANCEL"
      />
    </LayoutContent>
  );
}
